// src/hooks/notifications/useNotificationPreferences.js
import { useQuery, useMutation, useQueryClient } from "react-query";
import axios from "axios";
import { useAuth0 } from "@auth0/auth0-react";

const apiUrl = process.env.REACT_APP_API_URL || "";

// Fetch preferences function
const fetchNotificationPreferences = async (getAccessTokenSilently) => {
  const token = await getAccessTokenSilently();

  const { data } = await axios.get(`${apiUrl}/api/notifications/preferences`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  // Backend returns { success: true, data: { preferences: {...}, ... } }
  // Return the preferences data or default if not found
  if (data.success && data.data) {
    return data.data;
  }

  // Fallback to default preferences
  return {
    preferences: {
      follow: true,
      "message.liked": true,
      "message.replied": true,
    },
  };
};

/**
 * Hook to fetch notification preferences
 */
export const useNotificationPreferences = () => {
  const { getAccessTokenSilently } = useAuth0();

  return useQuery(
    "notificationPreferences",
    () => fetchNotificationPreferences(getAccessTokenSilently),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      enabled: !!getAccessTokenSilently, // Only run when authenticated
      retry: false, // Don't retry on failure to prevent rate limiting
      refetchOnWindowFocus: false, // Don't refetch on window focus
      refetchOnMount: false, // Don't refetch on component remount
      refetchOnReconnect: false, // Don't refetch on network reconnect
      notifyOnChangeProps: ["data", "error"], // Only notify on data/error changes
    }
  );
};

/**
 * Hook to update notification preferences
 */
export const useUpdateNotificationPreferences = () => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  return useMutation(
    async (preferences) => {
      const token = await getAccessTokenSilently();

      // Validate and sanitize preferences before sending
      if (!preferences || typeof preferences !== "object") {
        throw new Error("Invalid preferences object");
      }

      // Ensure we only send valid boolean preferences
      const sanitizedPreferences = {};
      const validPreferences = ["follow", "message.liked", "message.replied"];

      validPreferences.forEach((pref) => {
        if (
          preferences.hasOwnProperty(pref) &&
          typeof preferences[pref] === "boolean"
        ) {
          sanitizedPreferences[pref] = preferences[pref];
        }
      });

      // Ensure at least follow preference exists (for backward compatibility)
      if (!sanitizedPreferences.hasOwnProperty("follow")) {
        sanitizedPreferences.follow = true;
      }

      // Backend expects { preferences: { follow: true/false } }
      const payload = { preferences: sanitizedPreferences };

      // Debug logging to see what we're sending
      if (process.env.NODE_ENV === "development") {
        console.log("🔔 Sending notification preferences:", payload);
      }

      const { data } = await axios.put(
        `${apiUrl}/api/notifications/preferences`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      // Debug logging to see what we received
      if (process.env.NODE_ENV === "development") {
        console.log("🔔 Received notification preferences response:", data);
      }

      return data;
    },
    {
      onSuccess: (response) => {
        // Backend returns { success: true, data: { preferences: {...}, updatedAt: ... } }
        // Update the cache with the new preferences
        if (response.success && response.data) {
          queryClient.setQueryData("notificationPreferences", response.data);
        }
      },
      onError: (error) => {
        console.error("Failed to update notification preferences:", error);
      },
    }
  );
};
